/**
 * Client-side helper for error.tsx boundaries. Safe to call before Bugsnag has
 * started (in local dev with no key, isStarted() returns false and we no-op).
 */
import Bugsnag from "@bugsnag/js"

import { isDeploySkewError } from "@/lib/errors/deploy-skew"

export function notifyBoundaryError(boundary: string, error: Error & { digest?: string }): void {
  if (!Bugsnag.isStarted()) return
  // Deploy-skew errors ("Failed to find Server Action", chunk load failures)
  // all share one root cause — a client that pre-dates the current deploy.
  // Group them under a stable hash so one issue summarises every affected tab
  // instead of thousands of digest-unique events.
  const isSkew = isDeploySkewError(error)
  Bugsnag.notify(error, (event) => {
    event.context = boundary
    if (isSkew) {
      event.groupingHash = `deploy-skew:${boundary}`
      event.addMetadata("skew", {
        deploymentId: process.env.NEXT_PUBLIC_APP_VERSION,
      })
    }
    if (error.digest) {
      event.addMetadata("react", { digest: error.digest })
    }
  })
}

export type GraphQLErrorKind =
  | "http"
  | "invalid-json"
  | "graphql"
  | "no-data"
  | "network"
  | "timeout"

/**
 * Forward a failed GraphQL request to Bugsnag so backend schema drift, HTTP
 * failures, and timeouts surface in the dashboard (not just container stdout).
 * Grouping hash is stable per (operation, kind) so thousands of users hitting
 * the same schema error aggregate into one issue instead of thousands.
 * Redaction of `password` / `token` / etc. is handled by `redactedKeys` in
 * `bugsnag/config.ts` — anything passed via `variables` is scrubbed before send.
 */
export function notifyGraphQLError(params: {
  operationName: string
  kind: GraphQLErrorKind
  message: string
  httpStatus?: number
  errors?: Array<{ message: string; extensions?: Record<string, unknown> }>
  variables?: Record<string, unknown>
  cause?: unknown
}): void {
  if (!Bugsnag.isStarted()) return

  const error =
    params.cause instanceof Error
      ? params.cause
      : new Error(`[GraphQL:${params.kind}] ${params.operationName}: ${params.message}`)

  Bugsnag.notify(error, (event) => {
    event.context = `GraphQL:${params.operationName}`
    event.groupingHash = `graphql:${params.operationName}:${params.kind}`
    event.addMetadata("graphql", {
      operation: params.operationName,
      kind: params.kind,
      httpStatus: params.httpStatus,
      errors: params.errors,
      variables: params.variables,
    })
  })
}
